"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import BookingSummary, { BookingSummaryProps } from "./BookingSummary";
import { useGetAllCounsellorQuery } from "@/redux/features/appointment/appointmentApi";
import { TCounselor } from "@/types";
import { formatPractitionerTypes } from "@/lib/utils";

export interface Practitioner {
  id: string;
  name: string;
  role: string;
  image?: string;
  availableToday?: boolean;
  counselorId?: string; // Backend counselor ID
  nextAvailable?: string | null;
}

interface PractitionerSelectionProps {
  title?: string;
  subtitle?: string;
  practitioners?: Practitioner[];
  defaultSelectedId?: string;
  onPractitionerSelect?: (practitioner: Practitioner) => void;
  bookingSummary?: Omit<BookingSummaryProps, "practitionerName" | "practitionerImage">;
  divisionId?: string; // Backend division ID to filter practitioners
  serviceId?: string; // Backend service ID to filter practitioners
}

// Convert backend counselor to frontend practitioner
const counselorToPractitioner = (counselor: TCounselor): Practitioner => {
  // Check if available today based on availability or next_available
  const availableToday =
    counselor.availability?.today ||
    (counselor.next_available
      ? new Date(counselor.next_available).toDateString() ===
        new Date().toDateString()
      : false);

  return {
    id: counselor.id,
    name: counselor.name,
    role: counselor.specialization || formatPractitionerTypes(counselor.practitioner_type) || "Practitioner",
    image: counselor.profile_picture || undefined,
    availableToday,
    counselorId: counselor.id,
    nextAvailable: counselor.next_available,
  };
};

const defaultPractitioners: Practitioner[] = [
  {
    id: "practitioner-1",
    name: "Alexander Rodriguez",
    role: "Founder, Counsellor, Life Coach and Allied Health",
    image: "/images/v3/booking/practionerSelection/3ec39cb5db142e394a22480306f914fb6b63ac02.png",
    availableToday: true,
  },
  {
    id: "practitioner-2",
    name: "Sarah Mitchell",
    role: "Senior Counsellor",
    image: "/images/v3/booking/practionerSelection/c981fc25ae84d7fc1d5fd96585ebbfd5ad36a236.jpg",
    availableToday: true,
  },
  {
    id: "practitioner-3",
    name: "Emily Chen",
    role: "Psychologist & Therapist",
    image: "/images/v3/booking/practionerSelection/deaaef23e3aebd23f04b1782282538cd61090210.jpg",
    availableToday: true,
  },
];

// Practitioner Card Component
const PractitionerCard = ({
  practitioner,
  isSelected,
  onSelect,
}: {
  practitioner: Practitioner;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const imageSrc = practitioner.image || "/images/v3/booking/practionerSelection/practitioner-placeholder.jpg";

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={`Select ${practitioner.name}`}
      className={`
        group relative h-[280px] w-full overflow-hidden rounded-[20px] border
        text-left transition-all duration-300 ease-out sm:h-[400px]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-v3-primary focus-visible:ring-offset-2
        active:scale-[0.99]
        ${
          isSelected
            ? "border-v3-primary/20 shadow-[0px_0px_0px_10px_rgba(0,109,255,0.3)] scale-[1.02]"
            : "border-v3-primary/20 hover:scale-[1.02] hover:shadow-lg"
        }
      `}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src={imageSrc}
          alt=""
          fill
          className="-translate-y-[10px] object-cover object-top sm:translate-y-0 sm:object-center transition-transform duration-700 ease-out group-hover:scale-110"
          sizes="(max-width: 768px) 100vw, 492px"
        />
      </div>

      {/* Bottom gradient */}
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none rounded-[20px]"
        style={{
          paddingTop: "200px",
          background:
            "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 20%, rgba(0, 0, 0, 0.25) 40%, rgba(0, 0, 0, 0.42) 60%, rgba(0, 0, 0, 0.56) 80%, rgba(0, 0, 0, 0.65) 100%)",
        }}
      />

      {/* Selection Indicator - Checkmark */}
      <div
        className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-white/30 backdrop-blur-sm transition-all duration-1000 md:h-10 md:w-10 ${
          isSelected
            ? "scale-100 opacity-100 animate-[pulse_6s_ease-in-out_infinite]"
            : "scale-75 opacity-0"
        }`}
      >
        <svg
          className="h-4 w-4 text-white md:h-5 md:w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      {/* Content */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-4 md:gap-3.5 md:p-[30px]">
        {/* Availability Badge */}
        {practitioner.availableToday && (
          <div className="px-1.5 md:px-2.5">
            <span className="inline-flex items-center rounded-full bg-white/20 px-2.5 py-2 font-onest text-xs font-light text-white md:px-3.5 md:py-3 md:text-sm">
              Available Today
            </span>
          </div>
        )}

        {/* Name & Role */}
        <div className="flex flex-col">
          <span className="px-1.5 py-1.5 font-onest text-lg font-bold leading-[1.1] text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.9),0_1px_3px_rgba(0,0,0,0.8)] md:px-2.5 md:py-2.5 md:text-2xl">
            {practitioner.name}
          </span>
          <span className="px-1.5 py-1.5 font-onest text-sm font-semibold leading-[1.1] text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.9),0_1px_3px_rgba(0,0,0,0.8)] md:px-2.5 md:py-2.5 md:text-base">
            {practitioner.role}
          </span>
        </div>
      </div>
    </button>
  );
};

// Main Component
const PractitionerSelection = ({
  title = "Choose Your Practitioner",
  subtitle = "Search available occupational therapists and choose the right practitioner for your goals.",
  practitioners: propPractitioners,
  defaultSelectedId,
  onPractitionerSelect,
  bookingSummary,
  divisionId,
  serviceId,
}: PractitionerSelectionProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(
    defaultSelectedId || null
  );

  // Fetch counselors from API
  const {
    data: counselorsResponse,
    isLoading,
    error,
  } = useGetAllCounsellorQuery(
    {
      ...(divisionId && { division_id: divisionId }),
      ...(serviceId && { service_id: serviceId }),
    },
    { skip: !divisionId && !serviceId }
  );

  // Convert API counselors to practitioners, or use prop practitioners as fallback
  const practitioners: Practitioner[] = (() => {
    if (propPractitioners && propPractitioners.length > 0) {
      return propPractitioners;
    }

    if (counselorsResponse?.data) {
      const counselors = counselorsResponse.data as TCounselor[];
      return counselors
        .filter((c) => c.approved_by_admin && c.is_calendar_connected)
        .map(counselorToPractitioner);
    }

    return defaultPractitioners;
  })();

  // Reset selection when practitioners change
  useEffect(() => {
    if (defaultSelectedId) {
      setSelectedId(defaultSelectedId);
    }
  }, [defaultSelectedId, divisionId, serviceId]);

  const handleSelect = (practitioner: Practitioner) => {
    setSelectedId(practitioner.id);
    onPractitionerSelect?.(practitioner);
  };

  const selectedPractitioner = practitioners.find((p) => p.id === selectedId);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
        <div className="flex flex-col gap-6 md:gap-10 lg:w-[60%]">
          <div className="flex flex-col">
            <h2 className="p-2.5 font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
              {title}
            </h2>
            <p className="max-w-[565px] p-2.5 font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
              {subtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[350px] animate-pulse rounded-[20px] bg-gray-200 sm:h-[400px] md:h-[548px]"
              />
            ))}
          </div>
        </div>
        {bookingSummary && (
          <div className="w-full rounded-[10px] bg-white p-4 md:p-[30px] lg:w-[40%] lg:shrink-0">
            <BookingSummary
              {...bookingSummary}
              practitionerName={null}
              practitionerImage={null}
            />
          </div>
        )}
      </div>
    );
  }

  // Error state - fall back to default practitioners
  if (error) {
    console.error("Failed to fetch practitioners:", error);
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
      {/* Left Side: Header + Avatar Stack + Practitioner Cards */}
      <div className="flex flex-col gap-6 md:gap-10 lg:w-[60%]">
        {/* Header */}
        <div className="flex flex-col gap-4 md:gap-[30px]">
          <div className="flex flex-col">
            <h2 className="p-2.5 font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
              {title}
            </h2>
            <p className="max-w-[565px] p-2.5 font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
              {subtitle}
            </p>
          </div>
        </div>

        {/* Practitioner Cards */}
        <div
          className="grid grid-cols-1 gap-5 md:grid-cols-2"
          role="radiogroup"
          aria-label="Available practitioners"
        >
          {practitioners.map((practitioner) => (
            <PractitionerCard
              key={practitioner.id}
              practitioner={practitioner}
              isSelected={selectedId === practitioner.id}
              onSelect={() => handleSelect(practitioner)}
            />
          ))}
        </div>
      </div>

      {/* Right Side: Booking Summary */}
      {bookingSummary && (
        <div className="w-full rounded-[10px] bg-white p-4 md:p-[30px] lg:w-[40%] lg:shrink-0">
          <BookingSummary
            {...bookingSummary}
            practitionerName={selectedPractitioner?.name || null}
            practitionerImage={selectedPractitioner?.image || null}
          />
        </div>
      )}
    </div>
  );
};

export default PractitionerSelection;
